/**
 * Draggable map popup positioned in pixel space over the map container.
 */

/**
 * @param {HTMLElement} element
 * @param {import('ol/Map').default} map
 */
export function createDraggablePopup(element, map) {
    const mapEl = map.getTargetElement();
    let dragBound = false;

    function ensureInMap() {
        if (element.parentElement !== mapEl) {
            mapEl.appendChild(element);
        }
        element.style.position = 'absolute';
        element.style.margin = '0';
    }

    function clampPosition(left, top) {
        const width = element.offsetWidth || 320;
        const height = element.offsetHeight || 240;
        const maxLeft = Math.max(8, mapEl.clientWidth - width - 8);
        const maxTop = Math.max(8, mapEl.clientHeight - height - 8);
        return {
            left: Math.min(Math.max(8, left), maxLeft),
            top: Math.min(Math.max(8, top), maxTop),
        };
    }

    function setPosition(left, top) {
        const clamped = clampPosition(left, top);
        element.style.left = `${clamped.left}px`;
        element.style.top = `${clamped.top}px`;
    }

    function placeAtCoordinate(coordinate) {
        ensureInMap();
        element.classList.remove('d-none');

        const place = () => {
            const pixel = map.getPixelFromCoordinate(coordinate);
            if (!pixel) {
                return;
            }
            const width = element.offsetWidth || 320;
            const height = element.offsetHeight || 240;
            setPosition(pixel[0] - width / 2, pixel[1] - height - 16);
        };

        place();
        requestAnimationFrame(place);
    }

    function placeAtPixel(pixel) {
        ensureInMap();
        element.classList.remove('d-none');

        const place = () => {
            const width = element.offsetWidth || 320;
            const height = element.offsetHeight || 240;
            setPosition(pixel[0] - width / 2, pixel[1] - height - 16);
        };

        place();
        requestAnimationFrame(place);
    }

    function hide() {
        element.classList.add('d-none');
    }

    function isVisible() {
        return !element.classList.contains('d-none');
    }

    function bindDrag() {
        if (dragBound) {
            return;
        }
        dragBound = true;

        element.addEventListener('pointerdown', (event) => {
            const handle = event.target.closest('.pa-popup-drag-handle');
            if (!handle || event.target.closest('button, a, input, textarea, select, label')) {
                return;
            }

            event.preventDefault();

            const rect = element.getBoundingClientRect();
            const mapRect = mapEl.getBoundingClientRect();
            const startLeft = rect.left - mapRect.left;
            const startTop = rect.top - mapRect.top;
            const startX = event.clientX;
            const startY = event.clientY;

            handle.setPointerCapture(event.pointerId);
            element.classList.add('pa-popup-dragging');

            const onMove = (moveEvent) => {
                setPosition(
                    startLeft + (moveEvent.clientX - startX),
                    startTop + (moveEvent.clientY - startY),
                );
            };

            const onUp = (upEvent) => {
                handle.releasePointerCapture(upEvent.pointerId);
                element.classList.remove('pa-popup-dragging');
                handle.removeEventListener('pointermove', onMove);
                handle.removeEventListener('pointerup', onUp);
                handle.removeEventListener('pointercancel', onUp);
            };

            handle.addEventListener('pointermove', onMove);
            handle.addEventListener('pointerup', onUp);
            handle.addEventListener('pointercancel', onUp);
        });
    }

    bindDrag();

    return {
        placeAtCoordinate,
        placeAtPixel,
        hide,
        isVisible,
    };
}
